import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useI18n } from '../i18n/index.tsx';
import { useCan } from '../stores/auth.store.ts';
import { useCycleStore } from '../stores/cycle.store.ts';

/**
 * Which cycle the panel is about.
 *
 * Invisible while an organization has a single cycle, which is every screen
 * as it was before this existed. It appears once there are two — today, a
 * phase and a one-person extension of it — and starts on the default, so
 * nobody is moved anywhere they did not choose.
 */
export const CyclePicker = () => {
  const { t } = useI18n();
  const canSeeProgress = useCan('progress:read');
  const cycles = useCycleStore((state) => state.cycles);
  const selectedId = useCycleStore((state) => state.selectedId);
  const load = useCycleStore((state) => state.load);
  const select = useCycleStore((state) => state.select);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (canSeeProgress) void load();
  }, [canSeeProgress, load]);

  if (!canSeeProgress || cycles.length < 2 || !selectedId) return null;

  const change = (cycleId: string) => {
    select(cycleId);
    // A person's page belongs to one cycle; in another it would answer 404.
    // Their list is the nearest place that still means something.
    const detail = pathname.match(/^\/(participantes|evaluadores|coaches)\/[^/]+/);
    if (detail) navigate(`/${detail[1]}`);
  };

  return (
    <label className="mt-4 block rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
      <span className="block font-mono text-[9.5px] tracking-[0.12em] text-white/40 uppercase">
        {t('cycle.picker')}
      </span>
      <select
        value={selectedId}
        onChange={(event) => change(event.target.value)}
        className="mt-1.5 w-full cursor-pointer rounded-lg border border-white/15 bg-[var(--color-rail)] px-2 py-1.5 text-[12.5px] text-white/90 outline-none focus:border-white/40"
      >
        {cycles.map((cycle) => (
          <option key={cycle.id} value={cycle.id}>
            {cycle.name}
          </option>
        ))}
      </select>
    </label>
  );
};
